import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — Apex AV" },
      { name: "description", content: "Apex AV privacy policy: how we collect, use, and protect your information when you visit our site or request a Denver AV quote." },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: "Privacy Policy — Apex AV" },
      { property: "og:description", content: "How Apex AV collects, uses, and protects your information." },
      { property: "og:url", content: "https://apexav.com/privacy" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Privacy Policy — Apex AV" },
      { name: "twitter:description", content: "How Apex AV collects, uses, and protects your information." },
    ],
    links: [{ rel: "canonical", href: "https://apexav.com/privacy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  const updated = "October 20, 2026";

  return (
    <div className="container mx-auto px-6 py-16 md:py-24 max-w-3xl">
      <header className="mb-10">
        <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">
          Privacy Policy — How Apex AV Protects Your Information
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">Last updated: {updated}</p>
      </header>

      <div className="prose prose-invert max-w-none space-y-8 text-muted-foreground leading-relaxed">
        <section>
          <p>
            Apex AV ("we," "us," or "our") respects your privacy. This Privacy Policy explains
            how we collect, use, and protect information when you visit our website, request a
            quote, or engage us for home automation and AV installation services in the Denver
            metro area and Colorado Front Range.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">Information we collect</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong className="text-foreground">Contact details</strong> you submit through
              forms, email, or phone — name, email address, phone number, project address,
              and any details you share about your project.
            </li>
            <li>
              <strong className="text-foreground">Site usage data</strong> such as pages
              viewed, referring URL, browser type, device type, and approximate location,
              collected automatically through standard web logs and analytics.
            </li>
            <li>
              <strong className="text-foreground">Cookies</strong> used to keep the site
              functional and to measure aggregate traffic. You can disable cookies in your
              browser at any time.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">How we use your information</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>Respond to quote requests and customer inquiries.</li>
            <li>Schedule consultations, site visits, and installations.</li>
            <li>Send project updates, invoices, and service follow-ups.</li>
            <li>Improve our website, services, and customer experience.</li>
            <li>Comply with legal obligations.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">Sharing of information</h2>
          <p>
            We do not sell your personal information. We share information only with trusted
            service providers who help us operate our business (such as hosting, email, and
            scheduling tools), or when required by law. Any partners we use are bound by
            confidentiality obligations.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">Data retention &amp; security</h2>
          <p>
            We retain customer information for as long as needed to provide our services and
            meet legal, tax, and accounting requirements. We use reasonable administrative,
            technical, and physical safeguards to protect your information, but no system is
            completely secure.
          </p>
        </section>


        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">Analytics &amp; tracking technologies</h2>
          <p>
            We use standard web analytics tools to understand aggregate site traffic — which
            pages are visited, how visitors arrived at our site, and general device and
            location information. These tools rely on cookies and similar technologies
            described in our{" "}
            <a href="/cookies" className="text-brand-text underline underline-offset-2">Cookie Policy</a>.
            Analytics data is used internally to improve our website and is not sold to third
            parties.
          </p>
          <p className="mt-3">
            Google Tag Manager may deliver analytics and advertising tags. Where permitted,
            Google receives device information, pages visited, approximate location and cookie
            identifiers for advertising measurement and optimization. Analytics requires acceptance.
            Advertising tracking requires acceptance in consent-required or unknown regions;
            elsewhere it honors your opt-out. Use Cookie settings in the footer to change or
            withdraw your choice. Your browser retains a consent record identifying the choice,
            time, region classification, notice version and a random visitor reference, including
            earlier choices after withdrawal. This setup does not share customer contact details
            for enhanced conversions or customer matching. See{" "}
            <a href="https://business.safety.google/privacy/" className="text-brand-text underline underline-offset-2" target="_blank" rel="noopener noreferrer">Google’s privacy information</a>.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">Data requests</h2>
          <p>
            To request a copy of the personal information we hold about you, ask us to correct
            it, or ask us to delete it, email{" "}
            <a href="mailto:info@apexav.com" className="text-brand-text underline underline-offset-2">info@apexav.com</a>{" "}
            with your name and the email or phone number associated with your inquiry. We'll
            verify your identity before making changes and respond within a reasonable time.
            Some records, such as invoices and permits tied to completed installation work, may
            need to be retained for accounting or warranty purposes even after a deletion
            request.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">Your choices</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>You can request access to, correction of, or deletion of your personal information.</li>
            <li>You can opt out of marketing emails at any time using the unsubscribe link.</li>
            <li>You can decline cookies through your browser settings.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">Children's privacy</h2>
          <p>
            Our services are intended for adults. We do not knowingly collect personal
            information from children under 13.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">Changes to this policy</h2>
          <p>
            We may update this Privacy Policy from time to time. The "Last updated" date at
            the top of this page reflects the most recent changes.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">Contact us</h2>
          <p>
            Questions about this policy? Reach out to Apex AV at{" "}
            <a href="mailto:info@apexav.com" className="text-brand-text underline underline-offset-2">
              info@apexav.com
            </a>{" "}
            or{" "}
            <a href="tel:+13033173233" className="text-brand-text underline underline-offset-2">
              (303) 317-3233
            </a>
            , Denver, CO.
          </p>
        </section>
      </div>
    </div>
  );
}
